import {FormControl, FormGroup} from '@angular/forms';

import {BaseControl, SelectControl} from 'appShared/classes/controls';
import {
    ClientAccountBundleSummaryInterface,
    CurrencyTypesEnum,
    HttpStatusCodesEnum,
    ServerResponse,
    ServerResponseData,
    SesService,
    UserAccountSubscriptionInterface,
    UserAccountSubscriptionUpdateModel
} from '@spa/api';
import {Helper} from 'appShared/classes';
import {map} from 'rxjs/operators';
import {
    EditBundleSubscriptionControls,
    UpdteBundleSubscriptionActivationDateSelector,
    UpdteBundleSubscriptionCurrencySelector,
    UpdteBundleSubscriptionDeActivateDateSelector,
    UpdteBundleSubscriptionMarkupSelector,
    UpdteBundleSubscriptionPriceSelector,
} from 'appAssets/descriptors';
import {MarketDataFeedSubscriptionFormErrorStatesEnum} from 'appEntries/panel/enums';
import {BaseMarketDataForm} from 'appShared/forms';
import { CurrencyService } from 'appShared/services';
import { CurrencyTrait } from 'appEntries/panel/traits';
import { InArrayValidator } from '@spa/validators';

export class UpdateSubscriptionForm extends BaseMarketDataForm {
    private readonly clientAccountAndBundleData: ClientAccountBundleSummaryInterface;
    private readonly subscription: UserAccountSubscriptionInterface;
    private readonly offsetAccountId: string;
    private readonly currencyService: CurrencyService;

    constructor(
        sesService: SesService,
        currencyService: CurrencyService,
        subscription: UserAccountSubscriptionInterface,
        clientAccountAndBundleData: ClientAccountBundleSummaryInterface,
        offsetAccountId: string,
    ) {
        super(sesService);
        this.subscription = subscription;
        this.clientAccountAndBundleData = clientAccountAndBundleData;
        this.offsetAccountId = offsetAccountId;
        this.currencyService = currencyService;
    }
    
    private prepareCurrencyData(currencyControl: BaseControl): BaseControl {
        const currencies = []; 
        const control = currencyControl as SelectControl;
        control.options = CurrencyTrait.getSortedCurrencyAsOptions(this.currencyService.getAllCurrencies());
        currencies.push(...this.currencyService.getAllCurrencies().map(x => x.currencyCode)); 
        control.extendValidators(InArrayValidator(currencies));  
        control.disabled = control.options.length === 1;
        return control;
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        this.controls = EditBundleSubscriptionControls(this.subscription.active).map((control: BaseControl) => {
            switch (control.name) {
                case UpdteBundleSubscriptionActivationDateSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.subscription.startDate, control.validators));
                    break;
                case UpdteBundleSubscriptionDeActivateDateSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.subscription.endDate, control.validators));
                    break;
                case UpdteBundleSubscriptionPriceSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.subscription.price, control.validators));
                    break;
                case UpdteBundleSubscriptionMarkupSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.subscription.markup, control.validators));
                    break;
                case UpdteBundleSubscriptionCurrencySelector: 
                    var currencyControl = this.prepareCurrencyData(control);
                    this.formGroup.addControl(currencyControl.name, new FormControl(this.subscription.currency, currencyControl.validators));
                    break;
            }

            return control;
        });
    }

    createRequest(): ServerResponse<boolean> {
        if (!this.formGroup.dirty) {
            return null;
        }

        const activationDate = this.formGroup.value[UpdteBundleSubscriptionActivationDateSelector];
        const deActivationDate = this.formGroup.value[UpdteBundleSubscriptionDeActivateDateSelector];
        const price = this.getFormPriceValueByKey(UpdteBundleSubscriptionPriceSelector);
        const markup = this.getFormPriceValueByKey(UpdteBundleSubscriptionMarkupSelector);
        const currency =  this.formGroup.value[UpdteBundleSubscriptionCurrencySelector];
        const model = new UserAccountSubscriptionUpdateModel(
            currency,
            activationDate,
            deActivationDate,
            markup,
            price,
            markup > 0 ? this.offsetAccountId : null
        );

        return this.sesService.updateUserAccountSubscription(this.subscription.id, model).pipe(
            map((registerResponse: ServerResponseData<any>) => {
                if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<any>(
                        registerResponse,
                        MarketDataFeedSubscriptionFormErrorStatesEnum.UpdateMarketDataFeedSubscriptionFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }
}
